import { useEffect } from "react";
import { useLocation, Link } from "react-router-dom";
import { ArrowRight, Check } from "lucide-react";
import { SERVICES } from "@/lib/services";

export default function Services() {
  const { hash } = useLocation();

  useEffect(() => {
    if (hash) {
      const el = document.querySelector(hash);
      if (el) {
        setTimeout(() => el.scrollIntoView({ behavior: "smooth", block: "start" }), 100);
      }
    } else {
      window.scrollTo({ top: 0 });
    }
  }, [hash]);

  return (
    <main data-testid="services-page" className="bg-[#040812]">
      <section className="pt-32 md:pt-40 pb-16 md:pb-24 border-b border-white/5">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <span className="overline">Enterprise Practices</span>
          <h1 className="mt-6 font-display text-white text-5xl md:text-6xl lg:text-7xl tracking-tighter font-light leading-[1.02] max-w-4xl">
            Seven disciplines,
            <br />
            engineered for{" "}
            <span className="text-red-500">regulated enterprise</span>.
          </h1>
          <p className="mt-8 text-slate-400 text-base md:text-lg max-w-2xl leading-relaxed">
            Every practice is delivered by senior engineers, compliance
            specialists and operators with direct experience in banking,
            fintech and high-stakes global operations.
          </p>
        </div>
      </section>

      {SERVICES.map((svc, idx) => {
        const Icon = svc.icon;
        const reversed = idx % 2 === 1;
        return (
          <section
            key={svc.id}
            id={svc.id}
            data-testid={`services-section-${svc.id}`}
            className={`py-20 md:py-28 border-b border-white/5 ${
              idx % 2 === 0 ? "bg-[#040812]" : "bg-[#060B1A]"
            }`}
          >
            <div className="max-w-7xl mx-auto px-6 lg:px-10">
              <div
                className={`grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-start ${
                  reversed ? "lg:[&>*:first-child]:order-2" : ""
                }`}
              >
                <div className="lg:col-span-5">
                  <span className="font-mono text-[11px] text-red-500 tracking-[0.28em]">
                    /{svc.code}
                  </span>
                  <div className="mt-6 flex items-center gap-3">
                    <Icon size={22} className="text-red-500" strokeWidth={1.5} />
                    <div className="overline !text-slate-400 !text-[10px]">
                      Practice
                    </div>
                  </div>
                  <h2 className="mt-4 font-display text-white text-3xl md:text-4xl lg:text-5xl tracking-tighter font-light leading-tight">
                    {svc.title}
                  </h2>
                  <p className="mt-6 text-slate-400 text-base md:text-lg leading-relaxed">
                    {svc.long}
                  </p>
                  <Link
                    to="/contact"
                    className="mt-8 inline-flex items-center gap-2 text-white text-sm border-b border-white/20 hover:border-red-500 pb-1 group"
                  >
                    Discuss this practice
                    <ArrowRight
                      size={14}
                      className="transition-transform group-hover:translate-x-1"
                    />
                  </Link>
                </div>

                <div className="lg:col-span-7">
                  <div className="bg-[#0B132B]/50 border border-white/10 p-8 md:p-10">
                    <div className="overline mb-6">Capabilities</div>
                    <ul className="space-y-4">
                      {svc.capabilities.map((c) => (
                        <li
                          key={c}
                          className="flex items-start gap-3 text-slate-300 text-sm md:text-base border-b border-white/5 pb-4 last:border-0 last:pb-0"
                        >
                          <Check
                            size={16}
                            className="text-red-500 mt-1 shrink-0"
                            strokeWidth={2}
                          />
                          <span>{c}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </section>
        );
      })}

      <section className="py-24 md:py-32 bg-[#040812]">
        <div className="max-w-4xl mx-auto px-6 lg:px-10 text-center">
          <span className="overline">Next step</span>
          <h2 className="mt-6 font-display text-white text-4xl md:text-5xl tracking-tighter font-light leading-tight">
            Ready to scope an engagement?
          </h2>
          <Link
            to="/contact"
            data-testid="services-cta-button"
            className="mt-10 inline-flex items-center gap-2 bg-red-500 hover:bg-red-600 text-white font-medium px-7 py-4 rounded-sm transition-colors"
          >
            Schedule a Consultation
            <ArrowRight size={16} />
          </Link>
        </div>
      </section>
    </main>
  );
}
